import React from 'react';
import { areEqual } from 'react-window';
import { formatDate } from 'pano-utils';
import { useStoreMap } from 'effector-react';
import { ImageType, type TourAlbumItem, type TourItem } from '@types';
import type { ItemListRowProps } from '@components/ItemList';
import Image from '@components/Image';
import { CheckboxNoFunc } from '@components/Checkbox';
import { $tourData } from '@stores/dataStores';
import cn from '@utils/cn';
import ImgAlbum from './img-album.png';
import { useScenesCopyContext } from './scenesCopyContext';

interface Props extends ItemListRowProps {
  data: {
    albums: TourAlbumItem[];
    items: TourItem[];
  };
}

function ItemRow({ index, data, style }: Props) {
  const tid = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (tourData) => tourData?.tid,
  });
  const { targetTid, sort, query, setScenesCopyState } = useScenesCopyContext();
  const { albums, items } = data;
  const isAlbum = index < albums.length;
  if (isAlbum) {
    const item = albums[index];
    return (
      <div
        className="flex h-full cursor-pointer items-center border-b px-20 hover:bg-white-light"
        style={style}
        onClick={() => {
          setScenesCopyState({ albumId: item.id });
        }}
      >
        <div className="w-16 flex-shrink-0" />
        <Image src={ImgAlbum} size={32} className="ml-20 h-32 w-32" />
        <div className="mx-10 max-w-[330px] truncate" title={item.name}>
          {item.name}
        </div>
      </div>
    );
  }

  const item = items[index - albums.length];
  const dateText =
    !query && sort === 1
      ? formatDate(item.createDate)
      : formatDate(item.updateDate);

  const disabled = item.tid === tid;

  return (
    <div
      className={cn('flex h-full items-center border-b px-20', {
        'opacity-50': disabled,
        'cursor-pointer hover:bg-white-light': !disabled,
      })}
      style={style}
      onClick={() => {
        if (disabled) return;
        setScenesCopyState({ targetTid: item.tid });
      }}
    >
      <CheckboxNoFunc
        className="flex-shrink-0"
        checked={targetTid === item.tid}
      />
      <Image
        src={item.thumbUrl}
        size={32}
        type={ImageType.thumb}
        className="ml-20 h-32 w-32 cursor-pointer bg-white-border"
      />
      <div className="mx-10 max-w-[330px] truncate" title={item.name}>
        {item.name}
      </div>
      <div className="ml-auto flex shrink-0 items-center">
        {!!item.operatorName && (
          <div
            className="mr-20 max-w-[130px] shrink-0 truncate text-black-gray"
            title={item.operatorName}
          >
            {`归属人：${item.operatorName}`}
          </div>
        )}
        <div className="shrink-0 text-black-gray">{dateText}</div>
      </div>
    </div>
  );
}

export default React.memo(ItemRow, areEqual);
